import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

// Grid Balance admin email - only this user can access
const ADMIN_EMAIL = 'admin@gridbalance.de';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      console.log('📂 Loading user profile for:', userId);
      setProfileLoading(true);
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
        if (!error && data) {
          console.log('✅ Profile loaded successfully');
          setUserProfile(data);
        } else if (error) {
          console.warn('⚠️ Profile load error:', error?.message);
        }
      } catch (error) {
        console.error('❌ Profile load exception:', error);
      } finally {
        setProfileLoading(false);
      }
    },

    clear() {
      console.log('🗑️ Clearing user profile');
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      try {
        console.log('\n=== AUTH STATE CHANGE ===');
        console.log('Event:', event);
        console.log('Session exists:', !!session);
        console.log('User email:', session?.user?.email || 'none');
        console.log('User ID:', session?.user?.id || 'none');
        
        // Validate admin email on auth state change
        if (session?.user && session?.user?.email?.toLowerCase() !== ADMIN_EMAIL) {
          console.warn('❌ Non-admin user detected:', session?.user?.email);
          console.log('🚪 Signing out non-admin user');
          // Not admin - sign out immediately
          supabase?.auth?.signOut();
          setUser(null);
          profileOperations?.clear();
          setLoading(false);
          console.log('========================\n');
          return;
        }

        if (session?.user) {
          console.log('✅ Valid admin session detected');
        } else {
          console.log('ℹ️ No session (user signed out)');
        }

        setUser(session?.user ?? null);
        setLoading(false);
        
        if (session?.user) {
          profileOperations?.load(session?.user?.id); // Fire-and-forget
        } else {
          profileOperations?.clear();
        }
        console.log('========================\n');
      } catch (error) {
        console.error('❌ Auth state change error:', error);
        
        // Handle invalid refresh token error
        if (error?.message?.includes('Invalid Refresh Token') || error?.message?.includes('Refresh Token Not Found')) {
          console.log('🧹 Clearing corrupted session data from localStorage');
          try {
            localStorage.removeItem('gridbalance-auth-token');
            // Also clear Supabase's internal storage keys
            const storageKeys = Object.keys(localStorage)?.filter(key => 
              key?.startsWith('sb-') || key?.includes('supabase')
            );
            storageKeys?.forEach(key => {
              localStorage.removeItem(key);
              console.log('🗑️ Removed:', key);
            });
            console.log('✅ Corrupted session cleared successfully');
          } catch (clearError) {
            console.error('❌ Failed to clear localStorage:', clearError);
          }
        }
        
        // Reset auth state
        setUser(null);
        profileOperations?.clear();
        setLoading(false);
        console.log('========================\n');
      }
    }
  };

  useEffect(() => {
    console.log('\n🔄 AuthProvider mounted - initializing auth');
    
    // Initial session check
    console.log('📡 Checking for existing session...');
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      console.log('Initial session check result:', {
        hasSession: !!session,
        userEmail: session?.user?.email || 'none'
      });
      authStateHandlers?.onChange(null, session);
    })?.catch(error => {
      console.error('❌ Session check error:', error);
      
      // Handle invalid refresh token error
      if (error?.message?.includes('Invalid Refresh Token') || error?.message?.includes('Refresh Token Not Found')) {
        console.log('🧹 Clearing corrupted session data from localStorage');
        // Clear the corrupted auth token from localStorage
        try {
          localStorage.removeItem('gridbalance-auth-token');
          console.log('✅ Corrupted session cleared successfully');
        } catch (clearError) {
          console.error('❌ Failed to clear localStorage:', clearError);
        }
      }
      
      setLoading(false);
      setUser(null);
      profileOperations?.clear();
    });

    // CRITICAL: This must remain synchronous
    console.log('👂 Setting up auth state listener');
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    );
    console.log('✅ Auth state listener active\n');

    return () => {
      console.log('🔌 AuthProvider unmounting - cleaning up');
      subscription?.unsubscribe();
    };
  }, []);

  // Auth methods with comprehensive error handling
  const signIn = async (email, password) => {
    console.log('\n=== SIGN IN ATTEMPT ===');
    console.log('Timestamp:', new Date()?.toISOString());
    console.log('Email:', email);
    console.log('Password length:', password?.length || 0);
    
    try {
      // Input validation
      console.log('🔍 Step 1: Validating inputs...');
      if (!email || !password) {
        console.error('❌ Validation failed: Missing email or password');
        throw new Error('Email and password are required.');
      }
      console.log('✅ Inputs valid');

      // Validate admin email before attempting sign in
      console.log('🔍 Step 2: Checking admin email...');
      const normalizedEmail = email?.toLowerCase()?.trim();
      console.log('Normalized email:', normalizedEmail);
      console.log('Expected email:', ADMIN_EMAIL);
      console.log('Match:', normalizedEmail === ADMIN_EMAIL);
      
      if (normalizedEmail !== ADMIN_EMAIL) {
        console.error('❌ Admin validation failed');
        throw new Error('Access denied. Only Grid Balance admin can sign in.');
      }
      console.log('✅ Admin email validated');

      // Check Supabase client
      console.log('🔍 Step 3: Checking Supabase client...');
      console.log('Supabase client exists:', !!supabase);
      console.log('Supabase auth exists:', !!supabase?.auth);
      console.log('signInWithPassword method exists:', !!supabase?.auth?.signInWithPassword);
      console.log('signInWithPassword type:', typeof supabase?.auth?.signInWithPassword);
      
      if (!supabase?.auth?.signInWithPassword) {
        console.error('❌ CRITICAL: signInWithPassword method not available');
        throw new Error('Authentication system not properly initialized');
      }
      console.log('✅ Supabase client ready');

      // Attempt sign in
      console.log('🔍 Step 4: Calling Supabase signInWithPassword...');
      console.log('Request payload:', { email: normalizedEmail, password: '[REDACTED]' });
      
      const startTime = performance.now();
      const { data, error } = await supabase?.auth?.signInWithPassword({ 
        email: normalizedEmail, 
        password 
      });
      const endTime = performance.now();
      const duration = (endTime - startTime)?.toFixed(2);
      
      console.log(`⏱️ Request completed in ${duration}ms`);
      console.log('Response received:', {
        hasData: !!data,
        hasUser: !!data?.user,
        hasSession: !!data?.session,
        hasError: !!error,
        userEmail: data?.user?.email || 'none',
        userId: data?.user?.id || 'none'
      });
      
      if (error) {
        console.error('❌ Supabase returned error:', {
          message: error?.message,
          status: error?.status,
          name: error?.name,
          code: error?.code
        });
        
        // Handle specific Supabase errors
        if (error?.message?.includes('Invalid login credentials')) {
          throw new Error('Invalid login credentials. Please check your email and password.');
        } else if (error?.message?.includes('Email not confirmed')) {
          throw new Error('Please confirm your email address before signing in.');
        } else if (error?.message?.includes('Too many requests')) {
          throw new Error('Too many login attempts. Please wait a few minutes and try again.');
        } else {
          throw new Error(error.message || 'Failed to sign in. Please try again.');
        }
      }
      
      if (!data?.user) {
        console.error('❌ No user data received from Supabase');
        throw new Error('Login failed. No user data received.');
      }
      
      console.log('✅ Sign in successful!');
      console.log('User authenticated:', data?.user?.email);
      console.log('======================\n');
      
      return { data, error: null };
    } catch (error) {
      console.error('❌ Sign in exception:', {
        message: error?.message,
        name: error?.name,
        stack: error?.stack
      });
      console.log('======================\n');
      return { 
        data: null, 
        error: { 
          message: error?.message || 'An unexpected error occurred during sign in.' 
        } 
      };
    }
  };

  const signOut = async () => {
    console.log('\n=== SIGN OUT ATTEMPT ===');
    console.log('Timestamp:', new Date()?.toISOString());
    console.log('Current user:', user?.email || 'none');
    
    try {
      // Check if user is logged in
      if (!user) {
        console.log('ℹ️ No active session to sign out from');
        return { 
          error: null, 
          message: 'No active session to sign out from.' 
        };
      }

      // Check Supabase client
      console.log('🔍 Checking Supabase client...');
      console.log('Supabase auth exists:', !!supabase?.auth);
      console.log('signOut method exists:', !!supabase?.auth?.signOut);
      
      if (!supabase?.auth?.signOut) {
        console.error('❌ CRITICAL: signOut method not available');
        throw new Error('Authentication system not properly initialized');
      }

      // Attempt sign out
      console.log('🔍 Calling Supabase signOut...');
      const startTime = performance.now();
      const { error } = await supabase?.auth?.signOut();
      const endTime = performance.now();
      const duration = (endTime - startTime)?.toFixed(2);
      
      console.log(`⏱️ Request completed in ${duration}ms`);
      
      if (error) {
        console.error('❌ Sign out error:', error);
        // Handle specific sign out errors
        if (error?.message?.includes('Network')) {
          throw new Error('Network error. You may already be signed out.');
        } else {
          throw new Error(error.message || 'Failed to sign out. Please try again.');
        }
      }
      
      console.log('✅ Sign out successful');
      
      // Clear local state
      console.log('🗑️ Clearing local state...');
      setUser(null);
      profileOperations?.clear();
      console.log('✅ Local state cleared');
      console.log('=======================\n');
      
      return { error: null, success: true };
    } catch (error) {
      console.error('❌ Sign out exception:', error);
      
      // Even if sign out fails on server, clear local state
      console.log('🗑️ Clearing local state despite error...');
      setUser(null);
      profileOperations?.clear();
      console.log('=======================\n');
      
      return { 
        error: { 
          message: error?.message || 'An error occurred during sign out. Local session cleared.' 
        },
        success: false
      };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();
      if (!error && data) {
        setUserProfile(data);
      }
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};